import {
  computeModelData,
  fixedNumber,
  formatAmount,
  formatDate,
  getCleanedUrlParams,
} from '@utils';
import { INITIAL_VALUES, SELL_VEHICLE_AVAILABILITIES } from './constants';

const getVehicleUrl = (params) => {
  const urlParams = getCleanedUrlParams(params);
  const searchParams = new URLSearchParams(urlParams).toString();

  return `/sellVehicles?${searchParams}`;
};

const handleSellVehicles = (array) => {
  if (!array) {
    return [];
  }
  const newArray = [...array];
  array?.forEach((item, index) => {
    const row = {
      availability:
        SELL_VEHICLE_AVAILABILITIES[item.availability]?.label || '-',
      brand: item?.sku?.brand || '-',
      category: item?.sku?.category,
      edition: item?.sku?.edition?.trim() || '-',
      year: item?.sku?.year || '-',
      engineDisplacement: item?.sku?.engine?.displacement,
      fuelType: item?.sku?.engine?.fuelType,
      engineType: item?.sku?.engine?.type,
      image: item?.image?.url || item?.images?.[0]?.url || '',
      initialPrice: item.initialPrice
        ? formatAmount(fixedNumber(item.initialPrice))
        : '-',
      mileageFromOdometer: item.mileageFromOdometer || '-',
      model: item?.sku?.model || '-',
      name:
        item.name ||
        `${item?.sku?.brand} ${item?.sku?.model} ${item?.sku?.edition}` ||
        '-',
      price: item.price ? formatAmount(fixedNumber(item.price)) : '-',
      status: item.published ? 'Published' : 'Unpublished',
      published: item.published,
      skuId: item.skuId || '-',
      vehicleInternalId: item.vehicleInternalId || '-',
      internalId: item.internalId || '-',
      vin: item.vin || '-',
      plate: item.plate || '-',
      transmission: item?.sku?.transmission,
      createdAt: item.createdAt
        ? formatDate(item.createdAt, 'dd/MM/yyyy')
        : '-',
      updatedAt: item.updatedAt
        ? formatDate(item.updatedAt, 'dd/MM/yyyy')
        : '-',
      id: item.id,
      bodyType: item?.sku?.body?.type,
      powerHp: item?.sku?.engine?.powerHp || '-',
      batterySize: item?.sku?.engine?.batterySize || '-',
      euroClass: item?.sku?.engine?.euroClass,
    };

    newArray[index] = row;

    return newArray;
  });
  return newArray;
};

const formatEquipment = (equipment) => {
  if (!equipment) return equipment;

  const keys = ['comfort', 'exterior', 'interior', 'multimedia', 'safety'];
  const formatted = { ...equipment };

  keys.forEach((key) => {
    if (formatted[key]) {
      formatted[key] = {
        ...formatted[key],
        'el-GR': formatted[key]['el-GR']?.join?.('<br>') || '',
        'en-US': formatted[key]['en-US']?.join?.('<br>') || '',
      };
    }
  });

  return formatted;
};

// Generic enum finder
export const getEnumId = (enums, key, value, prop = 'name') => {
  if (!enums?.[key]?.length) return null;
  const match = enums[key].find((item) => item?.[prop] === value);
  return match?.id ?? null;
};

export const getFuelType = (enums, fuelType, prop = 'name') =>
  getEnumId(enums, 'fuelTypes', fuelType, prop);

export const getCategory = (enums, category, prop = 'name') =>
  getEnumId(enums, 'categories', category, prop);

export const getEuroClass = (enums, euroClass, prop = 'name') =>
  getEnumId(enums, 'euroClasses', euroClass, prop);

export const getEngineType = (enums, engineType, prop = 'name') =>
  getEnumId(enums, 'engineTypes', engineType, prop);

export const getTransmission = (enums, transmission, prop = 'name') =>
  getEnumId(enums, 'transmissions', transmission, prop);

export const getBodyType = (enums, bodyType, prop = 'name') =>
  getEnumId(enums, 'bodyTypes', bodyType, prop);

export const getBodySize = (enums, bodySize, prop = 'name') =>
  getEnumId(enums, 'bodySizes', bodySize, prop);

export const getProductLines = (enums, productLine, prop = 'name') =>
  getEnumId(enums, 'productLines', productLine, prop);

export const getDealer = (enums, dealer, prop = 'name') =>
  getEnumId(enums, 'dealers', dealer, prop);

export const normalizeVehicle = (data, enums) => {
  if (!data) return INITIAL_VALUES;

  return {
    vehicle: {
      ...data,
      exportCarGr: data.exportCarGr ?? false,
      skuId: data?.skuId?.slice(2) ?? data?.skuId,
      primaryImage: data?.selectedImageId
        ? data.images?.find((image) => image?.id === data.selectedImageId)?.url
        : data.images?.[0]?.url,
      sku: {
        ...data.sku,
        engine: {
          ...data.sku?.engine,
          fuelType: getFuelType(enums, data.sku?.engine?.fuelType),
          euroClass: getEuroClass(enums, data.sku?.engine?.euroClass),
          type: getEngineType(enums, data.sku?.engine?.type),
        },
        category: getCategory(enums, data.sku?.category),
        transmission: getTransmission(enums, data.sku?.transmission),
        body: {
          ...data.sku?.body,
          type: getBodyType(enums, data.sku?.body?.type),
          size: getBodySize(enums, data.sku?.body?.size),
        },
        productLine: getProductLines(enums, data.sku?.productLine),
        equipment: formatEquipment(data.sku?.equipment),
      },
    },
  };
};

const getOptions = (data = [], textProp = 'name', valueProp = 'id') => {
  return data.map((item) => {
    return {
      text: item[textProp],
      label: item[textProp],
      value: item[valueProp],
    };
  });
};

export const normalizeEnums = (data = {}) => {
  if (!data) return {};

  return {
    ...data,
    interiorTypes: data?.interiorTypes
      ? getOptions(Object.values(data.interiorTypes), 'name_el', 'slug')
      : undefined,
    transmissions: getOptions(data?.transmissions),
    engineTypes: getOptions(data?.engineTypes),
    fuelTypes: getOptions(data?.fuelTypes),
    euroClasses: getOptions(data?.euroClasses),
    availabilities: getOptions(data?.availabilities),
    bodyTypes: getOptions(data?.bodyTypes),
    bodySizes: getOptions(data?.bodySizes),
    categories: getOptions(data?.categories),
    colors: getOptions(data.colors?.data || [], 'name', 'name'),
    providers: data.providers?.data || [],
    models: data.models?.data || {},
    dealers: getOptions(data.dealers || [], 'name', 'name'),
    servicePoints: data.servicePoints?.data || [],
    ...computeModelData(data.models?.data),
  };
};
export const handleLeaseVehicleData = (data, enums) => {
  return {
    ...data,
    sku: {
      ...data?.properties,
      engine: {
        ...data?.properties?.engine,
        fuelType: getFuelType(enums, data.properties?.engine?.fuelType),
        euroClass: getEuroClass(enums, data.properties?.engine?.euroClass),
        type: getEngineType(enums, data.properties?.engine?.type),
      },
      category: getCategory(enums, data.properties?.category),
      transmission: getTransmission(enums, data.properties?.transmission),
      body: {
        ...data.properties?.body,
        type: getBodyType(enums, data.properties?.body?.type),
        size: getBodySize(enums, data.properties?.body?.size),
      },
      productLine: getProductLines(enums, data.properties?.productLine),
      equipment: formatEquipment(data.properties?.equipment),
    },
    skuId: data?.skuId?.slice(2),
  };
};

const handleEngineTypes = (engineTypes) => {
  if (!engineTypes) {
    return {};
  }
  return engineTypes.reduce((prev, curr) => {
    prev[curr.name] = curr.id;
    return prev;
  }, {});
};

const handleSantanderModelOptions = (array) => {
  if (!array || !array?.length) {
    return [];
  }

  const options = array.map((item) => ({
    text: item.title,
    value: item.id,
  }));

  return [
    {
      text: 'None',
      value: '',
    },
    ...options,
  ];
};

const handleVehiclesEnums = (data = {}) => {
  const availabilities = getOptions(data?.availabilities);
  const fuelTypes = getOptions(data?.fuelTypes);
  const allowedMonthlyKm = getOptions(data?.allowedMonthlyKm);
  const bodyTypes = getOptions(data?.bodyTypes);
  const bodySizes = getOptions(data?.bodySizes);
  const productLines = getOptions(data?.productLines);
  const categories = getOptions(data?.categories);
  const engineTypes = getOptions(data?.engineTypes);
  const transmissions = getOptions(data?.transmissions);
  const interiorTypes = Object.keys(data?.interiorTypes || {})?.map((key) => {
    return {
      name: data.interiorTypes[key].slug,
      label: data.interiorTypes[key].name,
      value: data.interiorTypes[key].slug,
    };
  });
  const brands = Object.keys(data?.models || {})?.map((key) => {
    return {
      text: data.models[key].name,
      value: data.models[key].slug,
      models:
        Object.keys(data.models[key]?.models || {})?.map((modelKey) => ({
          text: data.models[key]?.models[modelKey].name,
          value: data.models[key]?.models[modelKey].slug,
        })) || [],
    };
  });

  return {
    ...data,
    availabilities,
    fuelTypes,
    allowedMonthlyKm,
    transmissions,
    interiorTypes,
    bodyTypes,
    bodySizes,
    productLines,
    categories,
    engineTypes,
    brands,
  };
};
export {
  getVehicleUrl,
  handleSellVehicles,
  handleEngineTypes,
  handleSantanderModelOptions,
  handleVehiclesEnums,
};
